import isEmpty from 'lodash/isEmpty'
import { useBoolean } from 'react-use'
import { fetch, getWindow, hasWindow } from '@ztechapps/common.utils'
import { useAccessToken, useError } from '@ztechapps/common.hook'
import { useContext as useOrderContext } from '../Order/Context'

export type UsePayBalanceProps = {
  accessToken?: string
}

export type UsePayBalanceResult = {
  startPayment: () => Promise<void>
  isBusy: boolean
  error: string
  balance: number
}

enum PaymentRedirectStatusEnum {
  Success = 'success',
  Cancelled = 'cancelled',
}

const buildRedirectUrl = (paymentStatus: PaymentRedirectStatusEnum): string => {
  const currentUrl = new URL(getWindow()?.location?.href)
  currentUrl.searchParams.set('paymentStatus', paymentStatus)
  return currentUrl.href
}

/**
 * Charges the outstanding balance of a post-shipment order via Stripe hosted card
 * checkout — bypasses the cart wizard entirely, so no shipping mutation ever runs.
 * Redirects the browser to the returned Stripe session URL on success.
 *
 * Auth: prefers the caller-supplied `accessToken` (e.g. the invoice page's link token,
 * which lives inside the base64 `?params=` blob and is never read by `useAccessToken()`);
 * falls back to the signed-in session/URL token otherwise. `useAccessToken()` is called
 * unconditionally (hooks can't be called conditionally) and only consulted via `??` when
 * no explicit token is supplied.
 */
export const usePayBalance = ({ accessToken }: UsePayBalanceProps = {}): UsePayBalanceResult => {
  const { document } = useOrderContext()
  const sessionAccessToken = useAccessToken()
  const resolvedAccessToken = accessToken ?? sessionAccessToken
  const [isBusy, setBusy] = useBoolean(false)
  const { error, onError } = useError()

  const balance = document?.paymentBalance?.balance ?? 0

  const startPayment = async () => {
    try {
      setBusy(true)

      const { url }: { url?: string } = await fetch('/api/stripe/create-checkout-session', {
        method: 'POST',
        headers: { 'totalmilk-login-token': resolvedAccessToken },
        body: JSON.stringify({
          orderId: document?._id,
          userId: document?.by?._id,
          line_items: [{ name: `Balance payment for order ${document?.code}`, price: balance }],
          success_url: buildRedirectUrl(PaymentRedirectStatusEnum.Success),
          cancel_url: buildRedirectUrl(PaymentRedirectStatusEnum.Cancelled),
        }),
      })

      if (!isEmpty(url) && hasWindow()) {
        getWindow().location.href = url
      }
    } catch (e) {
      onError(e)
    } finally {
      setBusy(false)
    }
  }

  return { startPayment, isBusy, error, balance }
}
